import axios from 'axios';
import { v4 } from 'uuid';
import getConfig from 'next/config';
import { zipObject } from 'lodash';
import { setupInterceptors, getAccessManagementRoutes } from '../helpers';
import AccountManagementService from '../services/accountManagement/index.ts';
import { Analytics } from '../Analytics';

const { publicRuntimeConfig } = getConfig();
const HOST_URL = `${publicRuntimeConfig.webregServiceURL}/api/v1`;
const TEST_URL = `${publicRuntimeConfig.webregServiceURL}`;
const VERIFY_EMAIL_ENDPOINT = `${HOST_URL}/user/verify-email`;
export const REGISTRATION_URL = `${publicRuntimeConfig.registrationServiceURL}/api/v1/registration`;

let options = {};
if (publicRuntimeConfig.NEXT_PUBLIC_NODE_ENV === 'local') {
  options = { timeout: 30000 };
}
const axiosClient = axios.create(options);
setupInterceptors(axiosClient);

const NEXT_REDUX_STORE = 'NEXT_REDUX_STORE';
const getIpAddress = () => {
  if (typeof window !== 'undefined' && window[NEXT_REDUX_STORE]) {
    return window[NEXT_REDUX_STORE].getState().app.ipAddress;
  }
  return null;
};

const getEnhancedDebitEnabled = () => {
  if (typeof window !== 'undefined' && window[NEXT_REDUX_STORE]) {
    return window[NEXT_REDUX_STORE].getState().app.enhancedDebit
      .enhancedDebitEnabled;
  }
  return false;
};

const accessManagementRoutes = () =>
  getAccessManagementRoutes(
    window[NEXT_REDUX_STORE].getState().app.accessManagementEnabled
  );

function generateActions({
  SET_DEVICE_UUID,
  CHECK_PHONE,
  ACCESS_REQUEST,
  CONFIRM_CODE,
  VOICE_PIN,
  CHECK_VALID_EMAIL,
  LOGIN,
  GET_BANK_NAME,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  CREATE_ERROR,
  CLEAR_ERROR,
  SET_SMS_NOTIFICIATIONS_OPTED_STATUS,
  START_LOADING,
  STOP_LOADING,
  SET_NEXT_REG_STEP,
  SET_DOB,
  SET_ROLE,
  GET_USER,
  QUERY_GET_USER,
  QUERY_GET_INITIAL_FUNDING,
  SET_HIDE_FUNDING,
  INITIALIZE,
  ANSWER,
  COMPLETE,
  RESOLVE_ERROR,
  SAVE_DEBIT,
  SAVE_MANUAL_ACH,
  VERIFY_DEBIT_NOW,
  GET_BRAINTREE_TOKEN,
  GET_PLAID_TOKEN,
  GET_FUNDING_ACCOUNTS,
  VERIFY_DEBIT_LATER,
  GET_ANSWERS,
  SET_PREREGISTERED_USER,
  COMPLETE_RESET_KBA_FLOW,
  SET_MINIMUM_WALLET_LOAD_AMOUNT,
  GET_MICRODEPOSITS,
  DISPLAY_LEGAL_ADDRESS,
  SET_PLAID_OAUTH_MANUAL_OVERRIDE,
  SET_PLAID_OAUTH_ANDROID_LINK,
  SET_NATIVE_PLAID_FLOW_ENABLED,
  GET_PRICING_PLAN,
  SET_TOGGLES_LOADED,
  SET_IS_FB_PIXEL_ENABLED,
  INITIALIZE_IN_APP,
  REGISTRATION_REAUTH_APP_MODE,
  REGISTRATION_FAILED_APP_MODE,
  LAUNCH_NATIVE_PLAID_FLOW,
  POST_REG_COMPLETE,
  SET_PHONE_NUMBER_PROVIDED,
  SET_FIRST_NAME,
  SET_PARTNER,
  SET_PROMOTION_ID,
  SET_PARTNER_PARAMS,
  SET_SHORTCUT,
  SET_NO_PHONE_REG_ENTRY,
  SET_LINK_SOURCE,
  SET_EMAIL_BLUR_ENABLED,
  SET_IP_ADDRESS,
  SET_ACCESS_MANAGEMENT_ENABLED,
  SET_PROMO_CODE_VERIFICATION_ENABLED,
  VERIFY_PROMO_CODE,
  SET_PLAN_CHANGES_TERMS_AND_CONDITIONS,
  GET_PROVE_REDIRECT_URI,
  SET_CREDIT_WAITLIST,
  SET_SMS_NOTIFICATIONS_ENABLED,
  SET_PREFER_NOT_TO_SAY_OPTION_ENABLED,
  SET_APPROVER_INVITE_PROPERTIES,
  SET_NEW_DEACTIVATED_ERROR_MSG_ENABLED,
  SET_REQUIRE_TOKEN_FOR_APPROVERS_ENABLED,
  VALIDATE_PLACEHOLDER_TOKEN,
  SET_PMA_FINAL_TARGET,
  SET_PMA_STATE,
  SET_PROVE_MOBILE_AUTH_ENABLED,
  SET_MOCK_PMA,
  EMAIL_VERIFICATION,
  INVALIDATE_PMA,
  SET_ENHANCED_DEBIT_OPTIONS,
  SET_FEATURE_TOGGLES,
  SET_LAYOUT_TYPE
}) {
  let cancelToken;

  return {
    setDeviceUUID: deviceUUID => ({
      type: SET_DEVICE_UUID,
      payload: { deviceUUID }
    }),

    initialize: (
      accessToken,
      uniqueId,
      role,
      preregisteredUserId,
      authToken,
      dob,
      partnerSource,
      registrationId
    ) => ({
      type: INITIALIZE,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/initialize`,
        {
          accessToken,
          uniqueId,
          role,
          preregisteredUserId,
          authToken,
          dob,
          partnerSource,
          registrationId
        },
        {
          // params: { force_flow: 1 },
          headers: {
            'Access-Control-Allow-Origin': '*',
            'x-test-tracer': v4(),
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    answer: (accessToken, questions, answers, resolvesErrorId) => ({
      type: ANSWER,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/answer`,
        {
          questions,
          answers,
          resolvesErrorId
        },
        {
          headers: {
            'x-test-tracer': v4(),
            Authorization: `Bearer ${accessToken}`,
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4(),
            'client-ip-address': getIpAddress()
          }
        }
      ),
      originalQuestionsAndAnswer: zipObject(questions, answers)
    }),

    complete: accessToken => ({
      type: COMPLETE,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4()
          }
        }
      )
    }),

    resolveError: (accessToken, errorId) => ({
      type: RESOLVE_ERROR,
      promise: axiosClient.put(
        `${REGISTRATION_URL}/resolve-error?error_id=${errorId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    saveDebit: (accessToken, questions, answers, resolvesErrorId) => ({
      type: SAVE_DEBIT,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/answer?features[acquisitionfire_enhancedDebit]=${getEnhancedDebitEnabled()}`,
        {
          questions,
          answers,
          resolvesErrorId
        },
        {
          headers: {
            'x-test-tracer': v4(),
            Authorization: `Bearer ${accessToken}`,
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    saveManualAch: (accessToken, questions, answers, resolvesErrorId) => ({
      type: SAVE_MANUAL_ACH,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/answer`,
        {
          questions,
          answers,
          resolvesErrorId
        },
        {
          headers: {
            'x-test-tracer': v4(),
            Authorization: `Bearer ${accessToken}`,
            'segment-anonymous-id':
              window.analytics && typeof window.analytics.user === 'function'
                ? window.analytics.user().anonymousId()
                : Analytics.getAnonymousId() || v4()
          }
        }
      )
    }),

    verifyDebitNow: (instantFundingAccountId, amount1, amount2, token) => ({
      type: VERIFY_DEBIT_NOW,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/query-with-params?actionType=verify_instant_funding_account`,
        {
          params: {
            instant_funding_account_id: instantFundingAccountId,
            amount_1: amount1,
            amount_2: amount2
          }
        },
        {
          headers: {
            'x-test-tracer': v4(),
            Authorization: `Bearer ${token}`,
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    getBraintreeToken: accessToken => ({
      type: GET_BRAINTREE_TOKEN,
      promise: axiosClient.get(
        `${REGISTRATION_URL}/query?actionType=get_braintree_client_token`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-test-tracer': v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    getPlaidToken: (accessToken, platform = 'web') => ({
      type: GET_PLAID_TOKEN,
      promise: axiosClient.post(
        `${REGISTRATION_URL}/query-with-params?actionType=get_plaid_link_token`,
        {
          params: {
            platform
          }
        },
        {
          headers: {
            'x-test-tracer': v4(),
            Authorization: `Bearer ${accessToken}`,
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    getFundingAccounts: accessToken => ({
      type: GET_FUNDING_ACCOUNTS,
      promise: axiosClient.get(
        `${REGISTRATION_URL}/query?actionType=get_funding_accounts`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-test-tracer': v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    verifyDebitLater: () => ({
      type: VERIFY_DEBIT_LATER
    }),

    getAnswers: accessToken => ({
      type: GET_ANSWERS,
      promise: axiosClient.get(`${REGISTRATION_URL}/answers`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Pragma: 'no-cache',
          'x-test-tracer': v4(),
          'client-ip-address': getIpAddress()
        }
      })
    }),

    checkPhone: (phoneNumber, deviceUUID) => ({
      type: CHECK_PHONE,
      promise: axiosClient.post(
        accessManagementRoutes().phoneCheck,
        {
          phoneNumber,
          deviceUuid: deviceUUID,
          pinLength: 6
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      ),
      extras: phoneNumber
    }),

    accessRequest: (phoneNumber, deviceUUID) => ({
      type: ACCESS_REQUEST,
      promise: axiosClient.post(
        accessManagementRoutes().verify,
        {
          phoneNumber,
          deviceUuid: deviceUUID,
          pinLength: 6
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    login: (phoneNumber, password, accessToken) => ({
      type: LOGIN,
      promise: axiosClient.post(
        `${HOST_URL}/auth`,
        {
          phoneNumber,
          password,
          accessToken
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    voicePin: (phoneNumber, deviceUuid) => ({
      type: VOICE_PIN,
      promise: axiosClient.post(
        accessManagementRoutes().verifyVoice,
        {
          phoneNumber,
          deviceUuid
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    confirmCode: (phoneNumber, deviceUuid, confirmCode) => ({
      type: CONFIRM_CODE,
      promise: axiosClient.put(
        accessManagementRoutes().verifyConfirm,
        {
          phoneNumber,
          deviceUuid,
          confirmCode
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    getBankName: routingNumber => ({
      type: GET_BANK_NAME,
      promise: axiosClient.get(`${HOST_URL}/bank/${routingNumber}`, {
        headers: {
          'client-ip-address': getIpAddress()
        }
      })
    }),

    checkValidEmail: email => ({
      type: CHECK_VALID_EMAIL,
      promise: axiosClient.get(`${HOST_URL}/valid-email?email=${email}`, {
        headers: {
          'client-ip-address': getIpAddress()
        }
      })
    }),

    forgotPassword: (email, accessToken) => ({
      type: FORGOT_PASSWORD,
      promise: axiosClient.post(
        `${HOST_URL}/auth/forgot`,
        {
          email,
          accessToken
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    resetPassword: (
      userId,
      token,
      deviceUuid,
      newPassword,
      verifyPassword
    ) => ({
      type: RESET_PASSWORD,
      promise: axiosClient.post(
        `${HOST_URL}/auth/${userId}/reset`,
        {
          token,
          deviceUuid,
          newPassword,
          verifyPassword
        },
        {
          headers: {
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    setRole: role => ({
      type: SET_ROLE,
      payload: { role }
    }),

    setDOB: dob => ({
      type: SET_DOB,
      payload: { dob }
    }),

    createError: (message, registrationStep, error = true) => {
      window.scrollTo(0, 0);
      if (error) {
        Analytics.trackEvent('Registration Error Triggered', {
          eventProps: {
            registration_step: registrationStep,
            error_message: message
          }
        });
      }
      return {
        type: CREATE_ERROR,
        payload: { message, error }
      };
    },

    clearError: () => ({
      type: CLEAR_ERROR
    }),

    startLoading: isPlaid => ({
      type: START_LOADING,
      payload: { isPlaid }
    }),

    stopLoading: () => ({
      type: STOP_LOADING
    }),

    setNextRegStep: nextRegStep => ({
      type: SET_NEXT_REG_STEP,
      payload: { nextRegStep }
    }),

    getUser: token => ({
      type: GET_USER,
      promise: axiosClient.get(`${HOST_URL}/user`, {
        headers: {
          Authorization: token,
          'client-ip-address': getIpAddress()
        }
      })
    }),

    queryGetUser: accessToken => ({
      type: QUERY_GET_USER,
      promise: axiosClient.get(
        `${REGISTRATION_URL}/query?actionType=get_user`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-test-tracer': v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    queryGetInitialFunding: accessToken => ({
      type: QUERY_GET_INITIAL_FUNDING,
      promise: axiosClient.get(
        `${REGISTRATION_URL}/query?actionType=get_initial_funding`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-test-tracer': v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    setHideFunding: (hidePlaid, hideManualACH, hideDebit) => ({
      type: SET_HIDE_FUNDING,
      payload: { hidePlaid, hideManualACH, hideDebit }
    }),

    setPreregisteredUser: preregisteredUser => ({
      type: SET_PREREGISTERED_USER,
      payload: { preregisteredUser }
    }),

    completeResetKBAFlow: () => ({
      type: COMPLETE_RESET_KBA_FLOW
    }),

    setMinimumWalletLoad: (
      shouldShift,
      minimumLoadAmount,
      displayMinimumAmount
    ) => ({
      type: SET_MINIMUM_WALLET_LOAD_AMOUNT,
      payload: {
        shouldShift,
        minimumLoadAmount,
        displayMinimumAmount
      }
    }),

    getMicrodeposits: (accessToken, fundingAccountId, fundingAccountType) => ({
      type: GET_MICRODEPOSITS,
      promise: axiosClient.post(
        `${TEST_URL}/micro-deposit-discovery`,
        {
          funding_account_id: fundingAccountId,
          funding_type: fundingAccountType
        },
        {
          headers: {
            Authorization: accessToken,
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),

    setSmsNotificationsOptedStatus: optedStatus => ({
      type: SET_SMS_NOTIFICIATIONS_OPTED_STATUS,
      payload: {
        optedStatus
      }
    }),

    displayLegalAddress: display => ({
      type: DISPLAY_LEGAL_ADDRESS,
      payload: {
        display
      }
    }),

    setPlaidOAuthManualOverride: enabled => ({
      type: SET_PLAID_OAUTH_MANUAL_OVERRIDE,
      payload: {
        enabled
      }
    }),

    setPlaidOAuthAndroidLink: enabled => ({
      type: SET_PLAID_OAUTH_ANDROID_LINK,
      payload: {
        enabled
      }
    }),

    setNativePlaidFlowEnabled: () => ({
      type: SET_NATIVE_PLAID_FLOW_ENABLED
    }),

    getPricingPlan: authToken => ({
      type: GET_PRICING_PLAN,
      promise: AccountManagementService.getPricingPlan(authToken)
    }),

    setTogglesLoaded: loaded => ({
      type: SET_TOGGLES_LOADED,
      payload: { loaded }
    }),

    setIsFbPixelEnabled: enabled => ({
      type: SET_IS_FB_PIXEL_ENABLED,
      payload: { enabled }
    }),

    initializeInApp: params => ({
      type: INITIALIZE_IN_APP,
      payload: { ...params }
    }),

    registrationReauth: () => ({
      type: REGISTRATION_REAUTH_APP_MODE
    }),

    registrationFailed: params => ({
      type: REGISTRATION_FAILED_APP_MODE,
      payload: { appOs: params.appOs, error: params.error }
    }),

    launchNativePlaidFlow: () => ({
      type: LAUNCH_NATIVE_PLAID_FLOW
    }),

    postRegComplete: () => ({
      type: POST_REG_COMPLETE
    }),

    setPhoneNumberProvided: () => ({
      type: SET_PHONE_NUMBER_PROVIDED
    }),

    setFirstName: firstName => ({
      type: SET_FIRST_NAME,
      payload: { firstName }
    }),

    setPartner: (partner, registrationId) => ({
      type: SET_PARTNER,
      payload: { partner, registrationId }
    }),

    setPromotionId: promotionId => ({
      type: SET_PROMOTION_ID,
      payload: { promotionId }
    }),

    setPartnerParams: (
      partner,
      registrationId,
      phoneNumber,
      firstName,
      promotionId
    ) => ({
      type: SET_PARTNER_PARAMS,
      payload: { partner, registrationId, phoneNumber, firstName, promotionId }
    }),

    setShortcut: (shortcut, shortcutPlan) => ({
      type: SET_SHORTCUT,
      payload: { shortcut, shortcutPlan }
    }),

    setNoPhoneRegEntry: () => ({
      type: SET_NO_PHONE_REG_ENTRY
    }),

    setLinkSource: linkSource => ({
      type: SET_LINK_SOURCE,
      payload: { linkSource }
    }),

    setEmailBlurEnabled: () => ({
      type: SET_EMAIL_BLUR_ENABLED
    }),

    setIpAddress: ipAddress => ({
      type: SET_IP_ADDRESS,
      payload: {
        ipAddress
      }
    }),

    setAccessManagementEnabled: accessManagementEnabled => ({
      type: SET_ACCESS_MANAGEMENT_ENABLED,
      payload: {
        accessManagementEnabled
      }
    }),

    setPromoCodeVerificationEnabled: enabled => ({
      type: SET_PROMO_CODE_VERIFICATION_ENABLED,
      payload: {
        enabled
      }
    }),

    verifyPromoCode: (code, accessToken) => {
      if (typeof cancelToken !== typeof undefined) {
        cancelToken.cancel('Operation canceled due to new request.');
      }
      cancelToken = axios.CancelToken.source();

      return {
        type: VERIFY_PROMO_CODE,
        promise: axiosClient.get(
          `${REGISTRATION_URL}/verify-code/?code=${code}`,
          {
            cancelToken: cancelToken.token,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'x-test-tracer': v4(),
              'client-ip-address': getIpAddress()
            }
          }
        )
      };
    },

    setPlanChangesTermsAndConditions: () => ({
      type: SET_PLAN_CHANGES_TERMS_AND_CONDITIONS
    }),
    setProveMobileAuthEnabled: noPhoneNumberPriceText => ({
      type: SET_PROVE_MOBILE_AUTH_ENABLED,
      payload: { noPhoneNumberPriceText }
    }),

    getProveRedirectUri: mockPma => ({
      type: GET_PROVE_REDIRECT_URI,
      promise: axiosClient.get(`/api/prove-redirect-uri`, {
        headers: {
          'mock-pma-enabled': mockPma.enabled,
          'mock-pma-state': mockPma.state,
          'mock-pma-phone': mockPma.phone
        }
      })
    }),

    setCreditWaitlist: ({ enabled, showConfirmScreen }) => ({
      type: SET_CREDIT_WAITLIST,
      payload: {
        enabled,
        showConfirmScreen
      }
    }),

    setSmsNotificationsEnabled: defaultChecked => ({
      type: SET_SMS_NOTIFICATIONS_ENABLED,
      payload: { defaultChecked }
    }),

    setPreferNotToSayOptionEnabled: () => ({
      type: SET_PREFER_NOT_TO_SAY_OPTION_ENABLED
    }),

    setApproverInviteProperties: (phoneNumber, placeholderToken) => ({
      type: SET_APPROVER_INVITE_PROPERTIES,
      payload: {
        phoneNumber,
        placeholderToken
      }
    }),

    setNewDeactivatedErrorMsgEnabled: () => ({
      type: SET_NEW_DEACTIVATED_ERROR_MSG_ENABLED
    }),

    setRequireTokenForApproversEnabled: () => ({
      type: SET_REQUIRE_TOKEN_FOR_APPROVERS_ENABLED
    }),

    validatePlaceholderToken: (placeholderToken, userId, accessToken) => ({
      type: VALIDATE_PLACEHOLDER_TOKEN,
      promise: axiosClient.get(
        `${HOST_URL}/user/${userId}/pre-registered`,
        {
          params: {
            token: placeholderToken
          }
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-test-tracer': v4(),
            'client-ip-address': getIpAddress()
          }
        }
      )
    }),
    setPmaFinalTarget: (
      phoneNumber,
      deviceUuid,
      regAccessToken,
      nextRegStep
    ) => ({
      type: SET_PMA_FINAL_TARGET,
      payload: { phoneNumber, deviceUuid, regAccessToken, nextRegStep }
    }),

    setPmaState: state => ({
      type: SET_PMA_STATE,
      payload: { state }
    }),
    setMockPma: (state, phone) => ({
      type: SET_MOCK_PMA,
      payload: { state, phone }
    }),
    invalidatePma: () => ({
      type: INVALIDATE_PMA
    }),
    setEnhancedDebitOptions: (
      applePayEnabled,
      googlePayEnabled,
      threeDSEnabled,
      paypalEnabled,
      venmoEnabled
    ) => ({
      type: SET_ENHANCED_DEBIT_OPTIONS,
      payload: {
        applePayEnabled,
        googlePayEnabled,
        threeDSEnabled,
        venmoEnabled,
        paypalEnabled
      }
    }),
    emailVerification: ({ verificationToken, userId }) => ({
      type: EMAIL_VERIFICATION,
      promise: axios.post(VERIFY_EMAIL_ENDPOINT, {
        params: {
          tokenized_link: verificationToken,
          user_id: userId
        }
      })
    }),
    setFeatureToggles: featureToggles => ({
      type: SET_FEATURE_TOGGLES,
      payload: {
        featureToggles
      }
    }),
    setLayoutType: layoutType => ({
      type: SET_LAYOUT_TYPE,
      payload: {
        layoutType
      }
    })
  };
}

export default {
  consts: [
    'SET_DEVICE_UUID',
    'CREATE_ERROR',
    'CLEAR_ERROR',
    'START_LOADING',
    'STOP_LOADING',
    'VERIFY_DEBIT_LATER',
    'SET_NEXT_REG_STEP',
    'CLEAR_ERROR',
    'SET_ROLE',
    'SET_DOB',
    'SET_PREREGISTERED_USER',
    'SET_SMS_NOTIFICIATIONS_OPTED_STATUS',
    'SET_HIDE_FUNDING',
    'COMPLETE_RESET_KBA_FLOW',
    'SET_MINIMUM_WALLET_LOAD_AMOUNT',
    'DISPLAY_LEGAL_ADDRESS',
    'SET_PLAID_OAUTH_MANUAL_OVERRIDE',
    'SET_PLAID_OAUTH_ANDROID_LINK',
    'SET_NATIVE_PLAID_FLOW_ENABLED',
    'SET_TOGGLES_LOADED',
    'SET_IS_FB_PIXEL_ENABLED',
    'INITIALIZE_IN_APP',
    'REGISTRATION_REAUTH_APP_MODE',
    'REGISTRATION_FAILED_APP_MODE',
    'LAUNCH_NATIVE_PLAID_FLOW',
    'POST_REG_COMPLETE',
    'SET_PHONE_NUMBER_PROVIDED',
    'SET_FIRST_NAME',
    'SET_PARTNER',
    'SET_PROMOTION_ID',
    'SET_PARTNER_PARAMS',
    'SET_SHORTCUT',
    'SET_NO_PHONE_REG_ENTRY',
    'SET_LINK_SOURCE',
    'SET_EMAIL_BLUR_ENABLED',
    'SET_IP_ADDRESS',
    'SET_ACCESS_MANAGEMENT_ENABLED',
    'SET_PROMO_CODE_VERIFICATION_ENABLED',
    'SET_PLAN_CHANGES_TERMS_AND_CONDITIONS',
    'SET_CREDIT_WAITLIST',
    'SET_SMS_NOTIFICATIONS_ENABLED',
    'SET_PREFER_NOT_TO_SAY_OPTION_ENABLED',
    'SET_LOGIN_BACK_BUTTON_DISABLED',
    'SET_APPROVER_INVITE_PROPERTIES',
    'SET_NEW_DEACTIVATED_ERROR_MSG_ENABLED',
    'SET_REQUIRE_TOKEN_FOR_APPROVERS_ENABLED',
    'SET_PMA_FINAL_TARGET',
    'SET_PMA_STATE',
    'SET_PROVE_MOBILE_AUTH_ENABLED',
    'SET_MOCK_PMA',
    'INVALIDATE_PMA',
    'SET_ENHANCED_DEBIT_OPTIONS',
    'SET_FEATURE_TOGGLES',
    'SET_LAYOUT_TYPE'
  ],
  asyncConsts: [
    'CHECK_PHONE',
    'ACCESS_REQUEST',
    'LOGIN',
    'CONFIRM_CODE',
    'VOICE_PIN',
    'CHECK_VALID_EMAIL',
    'CONFIRM_ORDER',
    'FORGOT_PASSWORD',
    'RESET_PASSWORD',
    'GET_BANK_NAME',
    'GET_USER',
    'QUERY_GET_USER',
    'QUERY_GET_INITIAL_FUNDING',
    'INITIALIZE',
    'ANSWER',
    'COMPLETE',
    'RESOLVE_ERROR',
    'SAVE_DEBIT',
    'SAVE_MANUAL_ACH',
    'VERIFY_DEBIT_NOW',
    'GET_BRAINTREE_TOKEN',
    'GET_PLAID_TOKEN',
    'GET_FUNDING_ACCOUNTS',
    'GET_ANSWERS',
    'GET_MICRODEPOSITS',
    'GET_PRICING_PLAN',
    'VERIFY_PROMO_CODE',
    'GET_PROVE_REDIRECT_URI',
    'VALIDATE_PLACEHOLDER_TOKEN',
    'EMAIL_VERIFICATION'
  ],
  actions: generateActions
};
